/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { ReportData } from '../../../../types/reportData';
import { ChartType, InsightPage } from './constants';
import { loggerMessage } from '../../../../logger';
import { TableColumnType } from '../../../../types/universalTracker';
import { UniversalTrackerModalServiceUtrv } from '../../../../reducers/universal-tracker-modal';
import { getValueWithFallback, sortUtrvByDate } from '../../../../utils/charts';
import { UtrvType } from '../../../../constants/status';
import variables from '../../../../css/variables.module.scss';

interface FilterColumn {
  code: string,
  value: string[],
}

interface TableFilterOption {
  reportData: ReportData[] | undefined,
  utrCode: string,
  tableColumn: string
  filterColumn: FilterColumn
}

const getData = (reportData: ReportData[] | undefined, utrCode: string, tableColumn?: string): string[] | string | number | undefined => {
  if (!reportData) {
    return undefined;
  }
  const utrv = reportData.find(d => d.universalTracker.code === utrCode);
  if (!utrv) {
    return undefined;
  }

  if (tableColumn) {
    // Essentially we assume this is numeric
    const column = utrv.universalTracker.valueValidation?.table?.columns.find(c => c.code === tableColumn);

    const table = utrv.valueData?.table;
    if (!table) {
      return undefined;
    }
    if (column?.type === TableColumnType.Number) {
      return table.reduce((acc, col) => {
        const v = col.find(col => col.code === tableColumn);
        if (v?.value !== undefined) {
          return Number(v.value) + (acc ?? 0);
        }
        return acc;
      }, undefined as undefined | number);

    }

    return table[0]?.find(col => col.code === tableColumn)?.value;
  }

  if (!utrv.value && utrv.valueData?.table && utrv.valueData.table.length > 0) {
    loggerMessage(`Attempted to fetch a value from ${utrCode}, but saw table values:`, {
      level: 'info',
      table: utrv.valueData.table
    });
  }
  return utrv.value;
}

export const getDataAsNumberOrUndefined = (reportData: ReportData[] | undefined, utrCode: string, tableColumn?: string): number | undefined => {
  const data = getData(reportData, utrCode, tableColumn);
  if (data === undefined || isNaN(Number(data))) {
    return undefined;
  }
  return Number(data);
}

export const getDataAsNumber = (reportData: ReportData[] | undefined, utrCode: string, tableColumn?: string): number => {
  const data = getDataAsNumberOrUndefined(reportData, utrCode, tableColumn);
  if (data === undefined) {
    return 0;
  }
  return Number(data);
}

export const getDataAsValueData = (reportData: ReportData[] | undefined, utrCode: string): string | undefined => {
  if (!reportData) {
    return undefined;
  }
  const utrv = reportData.find(d => d.universalTracker.code === utrCode);
  return utrv?.valueData?.data as string ?? undefined;
}

export const getDataAsNumberTableSum = (reportData: ReportData[] | undefined, utrCode: string, tableColumn?: string): number => {
  if (!reportData) {
    return 0;
  }
  const utrv = reportData.find(d => d.universalTracker.code === utrCode);
  const sum = utrv?.valueData?.table?.reduce((acc, row) => {
    const col = row.find(col => col.code === tableColumn);
    return acc + Number(col?.value ?? 0);
  }, 0);
  return sum ?? 0;
}

interface GetDataAsPercentageTableSum {
  reportData: ReportData[] | undefined;
  utrCode: string;
  tableColumn: string;
  percentageColumn: string;
}
export const getDataAsPercentageTableSum = ({reportData, utrCode, tableColumn, percentageColumn}: GetDataAsPercentageTableSum): number => {
  if (!reportData) {
    return 0;
  }
  const utrv = reportData.find(d => d.universalTracker.code === utrCode);
  const sum = utrv?.valueData?.table?.reduce((acc, row) => {
    const col = row.find(col => col.code === tableColumn);
    const percentageCol = row.find(col => col.code === percentageColumn);
    const multipliedValue = Number(col?.value ?? 0) * (Number(percentageCol?.value ?? 0) / 100)
    return acc + multipliedValue;
  }, 0);
  return sum ?? 0;
}

export const sumByFilter = ({ reportData, utrCode, tableColumn, filterColumn }: TableFilterOption) => {
  if (!reportData) {
    return 0;
  }
  const utrv = reportData.find(d => d.universalTracker.code === utrCode);
  const table = utrv?.valueData?.table;
  if (!table) {
    return 0;
  }

  return table.reduce((acc, row) => {

    // Filter
    if (!row.some(col => col.code === filterColumn.code && filterColumn.value.some(v => v === col.value))) {
      return acc;
    }

    const col = row.find(col => col.code === tableColumn);
    return acc + Number(col?.value ?? 0);
  }, 0);
}

export const getDataAsNumberTableAverage = (reportData: ReportData[] | undefined, utrCode: string, tableColumn?: string): number => {
  if (!reportData) {
    return 0;
  }
  const utrv = reportData.find(d => d.universalTracker.code === utrCode);
  const rowCount = utrv?.valueData?.table?.length ?? 1;
  const sum = utrv?.valueData?.table?.reduce((acc, row) => {
    const col = row.find(col => col.code === tableColumn);
    return acc + Number(col?.value ?? 0);
  }, 0);
  return sum ? sum/rowCount : 0;
}

export const getInsightPageOptions = () => {

  const options = [
    {
      value: InsightPage.Environmental,
      label: 'Environmental'
    },
    {
      value: InsightPage.Social,
      label: 'Social'
    },
    {
      value: InsightPage.Governance,
      label: 'Governance'
    }
  ];

  return [
    {
      value: InsightPage.Overview,
      label: 'Overview'
    },
    ...options,
  ];
};

export const getInsightPage = (page: string | undefined) => {
  return page || InsightPage.Overview;
};

export const isInsightLayoutPage = (page: string): page is InsightPage => {
  switch (page) {
    case InsightPage.Environmental:
    case InsightPage.Social:
    case InsightPage.Governance:
      return true;
    default:
      return false;
  }
}

export const getLatestEffectiveDate = function (historicalReportData: { effectiveDate: string }[]) {
  return historicalReportData.reduce((d: string | undefined, reportData) => {
    return d === undefined || reportData.effectiveDate > d ? reportData.effectiveDate : d;
  }, undefined);
};

// @deprecated this function should be removed after SingleValueWrapper is cleaned up
export const getLatestDisplayValue = (
  utrvs: UniversalTrackerModalServiceUtrv[] | undefined,
  columnCode?: string
) => {
  if (!utrvs || utrvs.length <= 0) {
    return {
      actual: undefined,
      target: undefined,
      effectiveDate: '',
    };
  }
  const sortedUtrvs = utrvs.slice().sort(sortUtrvByDate).reverse();
  const latestActual = sortedUtrvs.find(utrv => utrv.type === UtrvType.Actual);
  const latestTarget = sortedUtrvs.find(utrv => utrv.type === UtrvType.Target);

  return {
    actual: latestActual ? getValueWithFallback(latestActual, columnCode, { fallback: undefined }) : undefined,
    target: latestTarget ? getValueWithFallback(latestTarget, columnCode, { fallback: undefined }) : undefined,
    effectiveDate: latestActual?.effectiveDate || latestTarget?.effectiveDate || '',
    period: latestActual?.period || latestTarget?.period || '',
  };
};

export const getSparklineChartProps = ({ isRestated }: { isRestated: boolean }) => ({
  legend: {},
  chartType: ChartType.LineChart,
  interpolateNulls: true,
  pointSize: 5,
  colors: [
    variables.NeutralsDark,
    variables.ChartBaseline,
    isRestated ? variables.ChartRestated : variables.ChartActual,
  ],
  lineWidth: 2,
  chartArea: {
    left: 10,
    top: 10,
    bottom: 10,
    right: 10,
  },
  backgroundColor: variables.BgToolbar,
  hAxis: {
    baselineColor: 'none',
    ticks: [],
  },
  vAxis: {
    baselineColor: 'transparent',
    ticks: [],
    minValue: 0,
  },
  series: {
    0: { type: 'line' },
    1: { type: 'line' },
  },
});
